import { unByKey } from 'ol/Observable.js';
import Overlay from 'ol/Overlay.js';
import { getArea, getLength } from 'ol/sphere.js';
import { LineString, Polygon, Circle } from 'ol/geom.js';
import Draw from 'ol/interaction/Draw.js';
import { transform } from 'ol/proj';
import { Vector as VectorLayer } from 'ol/layer.js';
import { Vector as VectorSource } from 'ol/source.js';
import { Circle as CircleStyle, Fill, Stroke, Style } from 'ol/style.js';
import { Feature } from 'ol';
import { groundDistance } from '@/components/mapTools/draw/drawMeasurements.js';

var tooltipOverlaysCircle = [];
var vertexCoordinateOverlays = [];

var sourceCircle = new VectorSource();

var mousePosition;
var isDrawing = false;
var finishTimer = null;

var vectorCircle = new VectorLayer({
  source: sourceCircle,
  style: new Style({
    fill: new Fill({
      color: 'rgba(255, 255, 255, 0.2)'
    }),
    stroke: new Stroke({
      color: '#ffcc33',
      width: 2
    }),
    image: new CircleStyle({
      radius: 7,
      fill: new Fill({
        color: '#ffcc33'
      })
    })
  })
});

var isVectorCircle = false;

/**
 * Currently drawn feature.
 * @type {module:ol/Feature~Feature}
 */
var sketch;

/**
 * The help tooltip element.
 * @type {Element}
 */
var helpTooltipElement;

/**
 * Overlay to show the help messages.
 * @type {module:ol/Overlay}
 */
var helpTooltipCircle;

/**
 * The measure tooltip element.
 * @type {Element}
 */
var measureTooltipCircleElementCircle;

/**
 * Overlay to show the measurement.
 * @type {module:ol/Overlay}
 */
var measureTooltipCircle;

/**
 * Message to show when the user is drawing a polygon.
 * @type {string}
 */
var continuePolygonMsg = 'Haga click para continuar dibujando el polígono';

/**
 * Message to show when the user is drawing a line.
 * @type {string}
 */
var continueLineMsg = 'Haga click para continuar dibujando la línea';

/**
 * Message to show when the user is drawing a circle.
 * @type {string}
 */
var continueCircleMsg = 'Haga doble click para terminar de dibujar';

var drawCircle;

var formatLength = function (line) {
  var length = getLength(line);
  var output = (Math.round(length * 100) / 100) + ' ' + 'm';
  return output;
};

var formatArea = function (polygon) {
  var area = getArea(polygon);
  var output = (Math.round(area * 100) / 100) + ' ' + 'm<sup>2</sup>';
  return output;
};

// El radio de una geometría Circle está en unidades de la proyección del mapa (EPSG:3857),
// no en metros de terreno: en Chile central eso infla el radio ~20% y el área ~44%, y el
// círculo no coincidía con el mismo área dibujada a mano (que sí usa ol/sphere). Medir sobre
// el terreno con el mismo helper que usa la herramienta de dibujo.
var radiusInMeters = function (circle) {
  var center = circle.getCenter();
  return groundDistance(center, [center[0] + circle.getRadius(), center[1]]);
};

var formatRadius = function (circle) {
  var radius = radiusInMeters(circle);

  // Calcular el área también
  var area = Math.PI * radius * radius;

  var output = (Math.round(radius * 100) / 100) + ' m (Radio)<br/>';
  output += (Math.round(area * 100) / 100) + ' m² (Área)';

  return output;
};

function pointerMoveRadius(evt) {
  if (evt.dragging) {
    return;
  }

  // Actualizar posición del mouse
  mousePosition = evt.coordinate;

  // Reiniciar temporizador para finalización automática
  if (isDrawing && sketch) {
    if (finishTimer) {
      clearTimeout(finishTimer);
    }

    // Si el usuario deja de mover el mouse por 2 segundos, finalizar el círculo
    finishTimer = setTimeout(function () {
      if (isDrawing && drawCircle) {
        console.log('Usuario dejó de mover el mouse, finalizando círculo');
        drawCircle.finishDrawing();
      }
    }, 2000); // 2 segundos
  }

  /** @type {string} */
  var helpMsg = 'Haga click para comenzar a dibujar el círculo';

  if (sketch) {
    var geom = (sketch.getGeometry());
    if (geom instanceof Polygon) {
      helpMsg = continuePolygonMsg;
    } else if (geom instanceof LineString) {
      helpMsg = continueLineMsg;
    } else if (geom instanceof Circle) {
      helpMsg = 'Haga doble click para finalizar el círculo';
    }
  }

  helpTooltipElement.innerHTML = helpMsg;
  helpTooltipCircle.setPosition(evt.coordinate);

  helpTooltipElement.classList.remove('hidden');
  helpTooltipElement.style.display = 'inherit';
}

function mouseOutRadius() {
  helpTooltipElement.classList.add('hidden');
  helpTooltipElement.style.display = 'none';
}

export const measureRadius = {
  data() {
    return {
      message: 'Hola'
    }
  },
  methods: {

    createVertexCoordinateTooltip: function (coordinate, map, label) {
      // Verificar que las coordenadas existan y sean válidas
      if (!coordinate || !Array.isArray(coordinate) || coordinate.length < 2) {
        console.warn('Coordenadas inválidas para el tooltip:', coordinate);
        return null;
      }

      // Crear elemento para mostrar coordenadas
      const element = document.createElement('div');
      element.className = 'tooltip tooltip-coordinate';
      element.style.pointerEvents = 'none'; // Asegurar que no intercepte clics

      // Intentar transformar las coordenadas a un formato geográfico legible si están en proyección
      let displayCoord = coordinate;

      try {
        // Si el mapa tiene una proyección diferente a WGS84, convertir
        const mapProjection = map.getView().getProjection();
        if (mapProjection && mapProjection.getCode() !== 'EPSG:4326') {
          displayCoord = transform(coordinate, mapProjection.getCode(), 'EPSG:4326');
        }
      } catch (e) {
        console.warn('Error transformando coordenadas:', e);
      }

      // Formatear con menos decimales si son números grandes
      const lon = Math.abs(displayCoord[0]) > 180 ?
        displayCoord[0].toFixed(2) : displayCoord[0].toFixed(6);
      const lat = Math.abs(displayCoord[1]) > 90 ?
        displayCoord[1].toFixed(2) : displayCoord[1].toFixed(6);

      // Si hay una etiqueta especial, mostrarla
      if (label) {
        element.innerHTML = `<div>${label}</div><div>Lon: ${lon}</div><div>Lat: ${lat}</div>`;
      } else {
        element.innerHTML = `<div>Lon: ${lon}</div><div>Lat: ${lat}</div>`;
      }

      // Crear overlay y posicionarlo
      const overlay = new Overlay({
        element: element,
        position: coordinate,
        offset: [0, 10],
        positioning: 'upper-center'
      });

      overlay.set('type', 'radius-coordinate');
      map.addOverlay(overlay);
      vertexCoordinateOverlays.push(overlay);

      return overlay;
    },

    checkDuplicateCoordinate: function (coordinate) {
      // Verificar que las coordenadas existan y sean válidas
      if (!coordinate || !Array.isArray(coordinate) || coordinate.length < 2) {
        return false;
      }

      // Usar una tolerancia para la comparación de coordenadas
      const tolerance = 0.000001; // Aproximadamente ~10 cm en coordenadas geográficas

      // Verificar si ya existe un overlay para estas coordenadas
      return vertexCoordinateOverlays.some(overlay => {
        const pos = overlay.getPosition();
        return pos &&
          Math.abs(pos[0] - coordinate[0]) < tolerance &&
          Math.abs(pos[1] - coordinate[1]) < tolerance;
      });
    },

    formatCircleArea: function (circle) {
      const radius = circle.getRadius();
      const area = Math.PI * radius * radius;

      let output = (Math.round(area * 100) / 100) + ' m²';

      return output;
    },

    injectCoordinateStyles: function () {
      // Verificar si los estilos ya existen
      const styleId = 'measure-radius-coordinate-styles';
      if (!document.getElementById(styleId)) {
        const styleElement = document.createElement('style');
        styleElement.id = styleId;
        styleElement.textContent = `
            .tooltip-coordinate {
              position: relative;
              background: rgba(0, 0, 0, 0.7);
              border-radius: 4px;
              color: white;
              padding: 4px 8px;
              opacity: 0.9;
              white-space: nowrap;
              font-size: 12px;
              pointer-events: none !important; /* Asegurar que no intercepta clics */
              z-index: 1000;
              max-width: 200px;
              text-align: center;
            }
            
            .tooltip-coordinate.tooltip-static {
              background-color: rgba(0, 0, 0, 0.7);
              color: white;
              border: 1px solid white;
              pointer-events: none !important; /* También para tooltips estáticos */
            }
          `;
        document.head.appendChild(styleElement);
      }
    },

    finishCircle: function () {
      if (drawCircle && typeof drawCircle.finishDrawing === 'function') {
        console.log('Finalizando círculo manualmente');
        drawCircle.finishDrawing();
        return true;
      }
      return false;
    },

    finishActiveCircle: function () {
      if (this.isDrawing && drawCircle) {
        drawCircle.finishDrawing();
        return true;
      }
      return false;
    },

    addInteraction: function () {
      console.log('Añadiendo interacción de círculo');
      var that = this;

      // Crear una nueva interacción de dibujo
      drawCircle = new Draw({
        source: sourceCircle,
        type: 'Circle',
        // Sin condiciones personalizadas que puedan causar problemas
        style: new Style({
          fill: new Fill({
            color: 'rgba(255, 255, 255, 0.2)'
          }),
          stroke: new Stroke({
            color: 'rgba(0, 0, 0, 0.5)',
            lineDash: [10, 10],
            width: 2
          }),
          image: new CircleStyle({
            radius: 5,
            stroke: new Stroke({
              color: 'rgba(0, 0, 0, 0.7)'
            }),
            fill: new Fill({
              color: 'rgba(255, 255, 255, 0.2)'
            })
          })
        })
      });

      // Añadir la interacción al mapa
      this.map.addInteraction(drawCircle);

      // Agregar evento de doble clic para finalizar el círculo
      this.dblClickListener = function (event) {
        if (that.isDrawing && sketch) {
          console.log('Doble clic detectado, finalizando círculo');
          drawCircle.finishDrawing();
        }
      };

      this.map.on('dblclick', this.dblClickListener);

      // Crear tooltips
      this.createMeasureTooltipCircle();
      this.createHelpTooltipCircle();

      this.createMeasureTooltipCircle();
      this.createHelpTooltipCircle();

      that.isDrawing = false;
      isDrawing = false;
      var listener;

      // Eventos
      drawCircle.on('drawstart', function (evt) {
        console.log('Comenzó dibujo de círculo');
        // Limpiar dibujo y tooltips anteriores antes de empezar el nuevo
        sourceCircle.clear();
        that.map.getOverlays().getArray().slice().forEach(overlay => {
          const type = overlay.get('type');
          if (type === 'radius' || type === 'radius-coordinate') {
            that.map.removeOverlay(overlay);
          }
        });
        tooltipOverlaysCircle = [];
        vertexCoordinateOverlays = [];

        that.isDrawing = true;
        isDrawing = true;
        sketch = evt.feature;

        var tooltipCoord = evt.coordinate;

        /* 
        // Mostrar coordenadas del centro
        if (evt.coordinate && Array.isArray(evt.coordinate)) {
          that.createVertexCoordinateTooltip(evt.coordinate, that.map, "Centro");
        }
        */

        // Radio de la circunferencia
        var radiusLine;

        listener = sketch.getGeometry().on('change', function (evt) {
          var geom = evt.target;
          var output;

          if (geom instanceof Circle) {
            output = formatRadius(geom);
            tooltipCoord = geom.getCenter();

            // Actualizar o crear el tooltip para el punto del perímetro
            var perimeterPoint = mousePosition;

            // Limpiar cualquier tooltip anterior del perímetro (excepto el central)
            for (let i = 1; i < vertexCoordinateOverlays.length; i++) {
              that.map.removeOverlay(vertexCoordinateOverlays[i]);
            }
            // Mantener solo el overlay del centro
            if (vertexCoordinateOverlays.length > 0) {
              vertexCoordinateOverlays = [vertexCoordinateOverlays[0]];
            }

            /* 
            // Crear nuevo tooltip para el perímetro
            if (perimeterPoint && Array.isArray(perimeterPoint)) {
              that.createVertexCoordinateTooltip(perimeterPoint, that.map, "Radio");
            }
            */

            // Dibujar línea que va desde el centro del círculo al perímetro
            var startPoint = geom.getCenter();

            if (radiusLine != undefined) {
              sourceCircle.removeFeature(radiusLine);
            }

            radiusLine = new Feature({
              geometry: new LineString([startPoint, mousePosition])
            });

            // Agregar nuevo radio
            sourceCircle.addFeature(radiusLine);

            // Reportar valor bruto (radio) al componente
            if (typeof that.updateRawValue === 'function') {
              that.updateRawValue(radiusInMeters(geom));
            }
          }

          measureTooltipCircleElementCircle.innerHTML = output;
          measureTooltipCircle.setPosition(tooltipCoord);
        });
      });

      drawCircle.on('drawend', function (evt) {
        console.log('Finalizó dibujo de círculo');
        that.isDrawing = false;
        isDrawing = false;

        // Limpiar temporizador de finalización automática
        if (finishTimer) {
          clearTimeout(finishTimer);
          finishTimer = null;
        }

        try {
          // Convertir tooltips de coordenadas a estáticos
          vertexCoordinateOverlays.forEach(overlay => {
            const element = overlay.getElement();
            if (element) {
              element.className = 'tooltip tooltip-coordinate tooltip-static';
              element.style.pointerEvents = 'none'; // Asegurar que no intercepte clics
            }
          });

          measureTooltipCircleElementCircle.className = 'tooltip tooltip-static';
          measureTooltipCircle.setOffset([0, -7]);

          // unset sketch
          sketch = null;
          // unset tooltip so that a new one can be created
          measureTooltipCircleElementCircle = null;
          that.createMeasureTooltipCircle();
          unByKey(listener);
        } catch (e) {
          console.error('Error al finalizar el dibujo del círculo:', e);
        }
      });
    },

    createHelpTooltipCircle: function () {
      if (helpTooltipElement) {
        helpTooltipElement.parentNode.removeChild(helpTooltipElement);
      }
      helpTooltipElement = document.createElement('div');
      helpTooltipElement.className = 'tooltip hidden';
      helpTooltipElement.style.pointerEvents = 'none'; // Asegurar que no intercepte clics
      helpTooltipCircle = new Overlay({
        element: helpTooltipElement,
        offset: [15, 0],
        positioning: 'center-left'
      });
      helpTooltipCircle.set('type', 'radius');
      this.map.addOverlay(helpTooltipCircle);
    },

    createMeasureTooltipCircle: function () {
      if (measureTooltipCircleElementCircle) {
        measureTooltipCircleElementCircle.parentNode.removeChild(measureTooltipCircleElementCircle);
      }
      measureTooltipCircleElementCircle = document.createElement('div');
      measureTooltipCircleElementCircle.className = 'tooltip tooltip-measure';
      measureTooltipCircleElementCircle.style.pointerEvents = 'none'; // Asegurar que no intercepte clics
      measureTooltipCircle = new Overlay({
        element: measureTooltipCircleElementCircle,
        offset: [0, -15],
        positioning: 'bottom-center'
      });
      measureTooltipCircle.set('type', 'radius');
      this.map.addOverlay(measureTooltipCircle);
      tooltipOverlaysCircle.push(measureTooltipCircle);
    },

    addMeasureRadiusInteraction: function () {
      // Inyectar estilos CSS para las coordenadas
      this.injectCoordinateStyles();

      if (!isVectorCircle) {
        this.map.addLayer(vectorCircle);
        isVectorCircle = true;
      }
      this.map.addEventListener('pointermove', pointerMoveRadius);
      this.map.getViewport().addEventListener('mouseout', mouseOutRadius);
      this.addInteraction();

      //Mostrar overlays existentes
      this.map.getOverlays().forEach(overlay => {
        const type = overlay.get('type');
        if (type == 'radius' || type == 'radius-coordinate') {
          const element = overlay.getElement();
          if (element) {
            element.style.display = 'inherit';
            element.style.pointerEvents = 'none'; // Asegurar que no intercepte clics
          }
        }
      });
    },

    removeMeasureRadiusInteraction: function (deleteDrawing) {
      // Remover interacción solo si existe
      if (drawCircle) {

        // Limpiar temporizador si existe
        if (finishTimer) {
          clearTimeout(finishTimer);
          finishTimer = null;
        }

        // Verificar que drawCircle sea una interacción real antes de eliminar
        if (drawCircle !== true && typeof drawCircle === 'object') {
          this.map.removeInteraction(drawCircle);
        }

        // Eliminar el escucha de doble clic
        if (this.dblClickListener) {
          this.map.un('dblclick', this.dblClickListener);
          this.dblClickListener = null;
        }

        // Establecer a null, no a true
        // Establecer a null, no a true
        drawCircle = null;
        this.isDrawing = false;
        isDrawing = false;

        this.map.removeEventListener('pointermove', pointerMoveRadius, false);
        this.map.getViewport().removeEventListener('mouseout', mouseOutRadius, false);
      }

      if (deleteDrawing) {
        isVectorCircle = false;
        this.map.removeLayer(vectorCircle);

        for (let i = 0; i < tooltipOverlaysCircle.length; i++) {
          this.map.removeOverlay(tooltipOverlaysCircle[i]);
        }

        // Eliminar también los overlays de coordenadas de vértices
        for (let i = 0; i < vertexCoordinateOverlays.length; i++) {
          this.map.removeOverlay(vertexCoordinateOverlays[i]);
        }

        // Force clean checks just in case arrays were desynced
        const overlays = this.map.getOverlays().getArray();
        const toRemove = [];
        overlays.forEach(overlay => {
          const type = overlay.get('type');
          if (type === 'radius' || type === 'radius-coordinate') {
            toRemove.push(overlay);
          }
        });
        toRemove.forEach(o => this.map.removeOverlay(o));

        //Vaciar tooltips
        tooltipOverlaysCircle = [];
        vertexCoordinateOverlays = []; // Limpiar array de coordenadas

        //Vector nuevo
        sourceCircle = new VectorSource();
        vectorCircle = new VectorLayer({
          source: sourceCircle,
          style: new Style({
            fill: new Fill({
              color: 'rgba(255, 255, 255, 0.2)'
            }),
            stroke: new Stroke({
              color: '#ffcc33',
              width: 2
            }),
            image: new CircleStyle({
              radius: 7,
              fill: new Fill({
                color: '#ffcc33'
              })
            })
          })
        });
      }
    },

    removeOnlyRadiusInteraction: function () {
      // Verificar que drawCircle sea una interacción real antes de eliminar
      if (drawCircle !== true && typeof drawCircle === 'object') {
        this.map.removeInteraction(drawCircle);
      }

      // Limpiar temporizador si existe
      if (finishTimer) {
        clearTimeout(finishTimer);
        finishTimer = null;
      }

      drawCircle = null;
      this.isDrawing = false;
      isDrawing = false;
      this.map.removeEventListener('pointermove', pointerMoveRadius, false);
      this.map.getViewport().removeEventListener('mouseout', mouseOutRadius, false);
    },

    cleanMeasureRadius: function () {
      console.log('Limpiando medición de radio y vértices');

      // 1. Buscar TODOS los overlays en el mapa que podrían ser de coordenadas
      const allOverlays = this.map.getOverlays().getArray();

      // 1. Remover la capa vectorial actual si existe
      if (isVectorCircle && vectorCircle) {
        this.map.removeLayer(vectorCircle);
        isVectorCircle = false;
      }
      // 2. Limpiar la fuente de datos
      if (sourceCircle) {
        sourceCircle.clear();
      }


      for (let i = allOverlays.length - 1; i >= 0; i--) {
        const overlay = allOverlays[i];
        const type = overlay.get('type');
        const element = overlay.getElement();

        // Eliminar cualquier overlay que:
        // 1. Tenga el tipo radius-coordinate
        // 2. O tenga la clase tooltip-coordinate 
        // 3. O contenga texto de coordenadas (Lat/Lon)
        // 4. O contenga texto de radio (Radio)
        // 5. O contenga texto de centro (Centro)
        // 6. O sea de tipo 'radius'
        if (
          type === 'radius-coordinate' ||
          type === 'radius' ||
          (element && element.className && element.className.includes('tooltip-coordinate')) ||
          (element && element.innerHTML && (
            element.innerHTML.includes('Lat:') ||
            element.innerHTML.includes('Lon:') ||
            element.innerHTML.includes('Radio') ||
            element.innerHTML.includes('Centro') ||
            element.innerHTML.includes('km²') ||
            element.innerHTML.includes('m²')
          ))
        ) {
          console.log('Eliminando overlay:', element && element.innerHTML);
          this.map.removeOverlay(overlay);
        }
      }

      // 2. Limpiar el array local
      vertexCoordinateOverlays = [];
      tooltipOverlaysCircle = [];

      // 3. Restablecer variables
      this.isDrawing = false;
      isDrawing = false;

      // 4. Limpiar la interacción actual si existe
      if (drawCircle !== true && typeof drawCircle === 'object') {
        this.map.removeInteraction(drawCircle);
      }
      drawCircle = null;

      // 5. Recrear la capa vectorial
      sourceCircle = new VectorSource();
      vectorCircle = new VectorLayer({
        source: sourceCircle,
        style: new Style({
          fill: new Fill({
            color: 'rgba(255, 255, 255, 0.2)'
          }),
          stroke: new Stroke({
            color: '#ffcc33',
            width: 2
          }),
          image: new CircleStyle({
            radius: 7,
            fill: new Fill({
              color: '#ffcc33'
            })
          })
        })
      });

      isVectorCircle = false;

      // 6. Añadir nueva interacción después de un pequeño delay para asegurar limpieza completa
      setTimeout(() => {
        this.addMeasureRadiusInteraction();
      }, 50);
    },

    setMeasureRadiusVisibility: function (isVisible) {
      if (isVisible) {
        this.map.addLayer(vectorCircle);
        //Mostrar overlays existentes
        this.map.getOverlays().forEach(overlay => {
          const type = overlay.get('type');
          const element = overlay.getElement();

          if (type === 'radius' || type === 'radius-coordinate' ||
            (element && element.className && element.className.includes('tooltip-coordinate'))) {
            if (element) {
              element.style.display = 'inherit';
              element.style.pointerEvents = 'none'; // Asegurar que no intercepte clics
            }
          }
        });
      } else {
        this.map.removeLayer(vectorCircle);
        //Ocultar overlays existentes
        this.map.getOverlays().forEach(overlay => {
          const type = overlay.get('type');
          const element = overlay.getElement();

          if (type === 'radius' || type === 'radius-coordinate' ||
            (element && element.className && element.className.includes('tooltip-coordinate'))) {
            if (element) element.style.display = 'none';
          }
        });
      }
    }
  }
}